import { MAX_UPLOAD_BYTES, UPLOAD_EXTENSIONS, type UploadView } from "@checkone/contract/client";
import { useId, useRef, useState } from "react";
import { useApiMutation } from "../../api/hooks";
import { ErrorState, Icon } from "../../components/ui";

/** The drop target: the whole box is the file input's label, so click, keyboard and drop all work. */
export function FileStep({ onUploaded, hint }: { onUploaded: (u: UploadView) => void; hint?: string }) {
  const create = useApiMutation("uploads.create");
  const [localError, setLocalError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const inputId = useId();
  const key = useRef(crypto.randomUUID?.() ?? String(Date.now()));

  const send = (file: File | undefined) => {
    setLocalError(null);
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (!(UPLOAD_EXTENSIONS as readonly string[]).includes(ext))
      return setLocalError("Upload an .xlsx, .xls or .csv file.");
    if (file.size > MAX_UPLOAD_BYTES) return setLocalError("Files must be 10 MB or smaller.");
    const form = new FormData();
    form.append("file", file);
    create.mutate(
      { formData: form, idempotencyKey: `${key.current}-${file.name}-${file.size}` } as Parameters<
        typeof create.mutate
      >[0],
      {
        onSuccess: (r) => onUploaded(r.upload),
      },
    );
  };

  return (
    <div className="stack">
      <label
        htmlFor={inputId}
        className="dropzone"
        data-active={drag}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          send(e.dataTransfer.files[0]);
        }}
      >
        <Icon name="upload" size={32} />
        <span className="title">Drop the file here, or click to choose</span>
        <span className="hint">{hint ?? ".xlsx, .xls or .csv · up to 10 MB"}</span>
        <input
          id={inputId}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="sr-only"
          onChange={(e) => send(e.target.files?.[0])}
          disabled={create.isPending}
        />
      </label>
      {create.isPending && (
        <p role="status" className="muted">
          Reading your file…
        </p>
      )}
      {localError && (
        <div className="callout danger" role="alert">
          {localError}
        </div>
      )}
      {create.error && <ErrorState error={create.error} />}
      <p className="subtle" style={{ margin: 0 }}>
        We look for a name column and an NTN or CNIC column; headers like "N.T.N", "Tax No", "Supplier" or
        "Party" are recognised. Keep identifiers as text in Excel: a CNIC shown as <code>3.52E+12</code> has
        lost digits, and we flag those rows rather than guess.
      </p>
    </div>
  );
}
